import React, { useState } from 'react';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import ProjectModal from '../components/ProjectModal';
import { Search, ZoomIn, X } from 'lucide-react';

const Gallery = () => {
  const [filter, setFilter] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const categories = ['All', 'Steel Plants', 'Power Plants', 'Metro', 'Foundations', 'Events', 'Solar'];

  const galleryImages = [
    {
      id: 1,
      src: "https://images.unsplash.com/photo-1693661391267-ad955aeeb564?q=80&w=2070&auto=format&fit=crop",
      category: "Steel Plants",
      title: "JSPL Angul Expansion",
      location: "Angul, Odisha"
    },
    {
      id: 2,
      src: "https://images.unsplash.com/photo-1624027492684-327af1fb7559?q=80&w=2070&auto=format&fit=crop",
      category: "Steel Plants",
      title: "Blast Furnace Unit 2",
      location: "Raigarh"
    },
    {
      id: 3,
      src: "https://images.unsplash.com/photo-1761414500570-0b835cdcf3c8?q=80&w=2070&auto=format&fit=crop",
      category: "Power Plants",
      title: "Thermal Power Station",
      location: "Tamnar"
    },
    {
      id: 4,
      src: "https://images.unsplash.com/photo-1687580712205-8f15015e1776?q=80&w=2070&auto=format&fit=crop",
      category: "Metro",
      title: "Pune Metro Station",
      location: "Pune"
    },
    {
      id: 5,
      src: "https://images.unsplash.com/photo-1580901369227-308f6f40bdeb?q=80&w=2070&auto=format&fit=crop",
      category: "Foundations",
      title: "Deep Piling Works",
      location: "Mumbai"
    },
    {
      id: 6,
      src: "/assets/safety week.jpg",
      category: "Events",
      title: "Safety Week Celebration",
      location: "Head Office"
    },
    {
      id: 7,
      src: "https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?q=80&w=2070&auto=format&fit=crop",
      category: "Foundations",
      title: "Rock Anchoring Site",
      location: "Lonavala"
    },
    {
      id: 8,
      src: "https://images.unsplash.com/photo-1649617413676-28b74f1ac4d3?q=80&w=2070&auto=format&fit=crop",
      category: "Metro",
      title: "Viaduct Construction",
      location: "Pune"
    },
    {
      id: 9,
      src: "https://images.unsplash.com/photo-1722544133021-68bd0de62e95?q=80&w=2070&auto=format&fit=crop",
      category: "Power Plants",
      title: "Cooling Tower Base",
      location: "Nagpur"
    },
    {
      id: 10,
      src: "/assets/image (1).jpg",
      category: "Solar",
      title: "Solar Installation Gallery",
      location: "Multiple Locations",
      images: [
        "/assets/image (1).jpg",
        "/assets/image (2).jpg",
        "/assets/image (3).jpg",
        "/assets/image (4).jpg",
        "/assets/image (5).jpg",
        "/assets/image (6).jpg",
        "/assets/image (7).jpg",
        "/assets/image (8).jpg",
        "/assets/image (9).jpg",
        "/assets/image (10).jpg",
        "/assets/image (11).jpg",
        "/assets/image (12).jpg"
      ]
    },
    {
      id: 11,
      src: "/assets/image (1).png",
      category: "",
      title: "",
      location: ""
    },
    {
      id: 12,
      src: "/assets/image (2).png",
      category: "",
      title: "",
      location: ""
    },
    {
      id: 13,
      src: "/assets/image (3).png",
      category: "",
      title: "",
      location: ""
    }
  ];

  const filteredImages = filter === 'All'
    ? galleryImages
    : galleryImages.filter(img => img.category === filter);

  return (
    <Layout>
      <PageHeader
        title="Project Gallery"
        subtitle="Visualizing Excellence"
        image="https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=2070&auto=format&fit=crop"
      />

      <section className="py-24 bg-background min-h-screen">
        <div className="container mx-auto px-4">

          {/* Filter Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-16" data-aos="fade-up">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setFilter(cat)}
                className={`px-6 py-2 rounded-full text-sm font-bold tracking-wider transition-all duration-300 ${
                  filter === cat
                    ? 'bg-primary text-background shadow-[0_0_20px_rgba(212,175,55,0.4)]'
                    : 'bg-card border border-white/10 text-muted-foreground hover:border-primary hover:text-primary'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredImages.map((img, index) => (
              <div
                key={img.id}
                className="group relative aspect-[4/3] rounded-xl overflow-hidden cursor-pointer border border-white/5 hover:border-primary/50 transition-all duration-500"
                data-aos="fade-up"
                data-aos-delay={index * 50}
                onClick={() => {
                  if (img.images) {
                    setSelectedProject(img);
                    setIsModalOpen(true);
                  } else {
                    setSelectedImage(img);
                  }
                }}
              >
                <img
                  src={img.src}
                  alt={img.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                  <span className="text-primary text-xs font-bold uppercase tracking-wider mb-1">{img.category}</span>
                  <h3 className="text-xl font-serif font-bold text-white mb-1">{img.title}</h3>
                  <p className="text-sm text-white/70 flex items-center gap-1">
                    <span className="w-1 h-1 bg-primary rounded-full"></span> {img.location}
                  </p>
                  <div className="absolute top-4 right-4 w-10 h-10 bg-background/20 backdrop-blur-md rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-all duration-500 delay-100 transform translate-y-4 group-hover:translate-y-0">
                    <ZoomIn size={20} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {selectedProject && (
        <ProjectModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={selectedProject.title}
          images={selectedProject.images}
        />
      )}

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-[100] bg-background/95 backdrop-blur-xl flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-8 right-8 text-white/50 hover:text-primary transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            <X size={40} />
          </button>

          <div
            className="relative max-w-5xl w-full max-h-[90vh] rounded-lg overflow-hidden shadow-2xl border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.src}
              alt={selectedImage.title}
              className="w-full h-full object-contain max-h-[80vh]"
            />
            <div className="bg-card p-6 border-t border-white/10 flex justify-between items-center">
              <div>
                <h3 className="text-2xl font-serif font-bold text-white">{selectedImage.title}</h3>
                <p className="text-primary">{selectedImage.location}</p>
              </div>
              <span className="px-4 py-1 rounded-full border border-white/10 text-sm text-muted-foreground">
                {selectedImage.category}
              </span>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Gallery;
